import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";

export default function Privacy() {
  return (
    <main className="min-h-screen bg-[#FDFBF7] text-[#1C1917]" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-6 md:px-12 pt-32 pb-24">
        <Link to="/" data-testid="privacy-back" className="inline-flex items-center gap-2 text-sm text-[#57534E] hover:text-[#3A5A40] transition-colors mb-12">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to home
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-5 h-5 text-[#3A5A40]" strokeWidth={1.5} />
          <p className="overline text-[#9F5C45]">Privacy & HIPAA notice</p>
        </div>
        <h1 className="font-serif font-light text-5xl md:text-6xl tracking-tight mb-10 leading-[1.05]">
          How we hold what you share.
        </h1>
        <p className="text-[#57534E] text-sm mb-10">Last updated: December 2025</p>

        <div className="prose-styles space-y-10 text-[#1C1917]">
          <Section title="The short version">
            <p>
              Let Go Wellness, PLLC is a HIPAA-covered mental health practice. Everything you share with us — on this
              website, in session, or in writing — is treated as Protected Health Information (PHI) and is held in
              strict confidence. We do not sell, rent, or share your information for marketing purposes. Ever.
            </p>
          </Section>

          <Section title="What we collect through this site">
            <ul className="list-disc list-outside ml-5 space-y-2">
              <li>Information you submit through the new-patient enrollment form (name, contact details, primary concern, insurance info if you share it, and an optional message).</li>
              <li>Payment information when you self-pay for a session — processed by Stripe; we never see or store your card number.</li>
              <li>Basic anonymous analytics (page views, time on site) to improve the website.</li>
            </ul>
          </Section>

          <Section title="How we use what you share">
            <ul className="list-disc list-outside ml-5 space-y-2">
              <li>To match you with the right therapist and contact you about scheduling.</li>
              <li>To verify your insurance benefits, when applicable.</li>
              <li>To send you appointment confirmations, receipts, and (if requested) superbills.</li>
              <li>To comply with our legal and ethical obligations as licensed clinicians.</li>
            </ul>
          </Section>

          <Section title="Who has access">
            <p>
              Only the staff and clinicians of Let Go Wellness — and the secure systems we use to run the practice
              (HIPAA-compliant EHR, encrypted email, Stripe for payments). We sign Business Associate Agreements (BAAs)
              with every vendor that touches PHI.
            </p>
          </Section>

          <Section title="Your rights">
            <ul className="list-disc list-outside ml-5 space-y-2">
              <li>You can request a copy of any records we hold about you.</li>
              <li>You can ask us to correct anything that's inaccurate.</li>
              <li>You can request that we delete your information (except where law requires us to retain clinical records).</li>
              <li>You can opt out of any non-essential communication at any time.</li>
            </ul>
          </Section>

          <Section title="Mandatory reporting (the limits of confidentiality)">
            <p>
              The only situations in which we are legally required to disclose information are: imminent danger to
              yourself or another person, suspected abuse of a minor, elderly person, or dependent adult, or a valid
              court order. We will tell you if any of these apply.
            </p>
          </Section>

          <Section title="Contact us about your privacy">
            <p>
              For any questions about this notice or to exercise your rights, email{" "}
              <a href="mailto:Letgowellness1@gmail.com" className="text-[#3A5A40] underline underline-offset-4">Letgowellness1@gmail.com</a>{" "}
              or call <a href="tel:2034378193" className="text-[#3A5A40] underline underline-offset-4">203-437-8193</a>. We respond to every privacy request within 30 days.
            </p>
          </Section>

          <Section title="In crisis?">
            <p>
              If you are in immediate crisis, please call or text <a href="tel:988" className="text-[#3A5A40] underline underline-offset-4">988</a> — the 988 Suicide &amp; Crisis Lifeline — or go to your nearest emergency room. This website is not monitored 24/7.
            </p>
          </Section>
        </div>
      </div>
    </main>
  );
}

const Section = ({ title, children }) => (
  <section>
    <h2 className="font-serif text-2xl md:text-3xl tracking-tight mb-4">{title}</h2>
    <div className="text-[#1C1917] text-base leading-relaxed">{children}</div>
  </section>
);
